import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { Section, Eyebrow } from "./Primitives";
import { Stagger, StaggerItem } from "./Reveal";
import { CASES } from "../../data/content";

const PLACEHOLDERS = [
  { sector: "F&B", hint: "Multi-outlet operations, unified into one live picture.", caseNo: "Case 02" },
  { sector: "Fintech", hint: "Month-end reconciliation without the scramble.", caseNo: "Case 03" },
];

function RealCard({ c }) {
  return (
    <Link
      to={`/work/${c.slug}`}
      data-testid="case-card-0"
      className="group relative flex min-h-[360px] flex-col justify-between rounded border border-line bg-surface-2 p-8 text-content transition-transform duration-500 hover:-translate-y-1.5 md:p-9"
    >
      <div className="flex items-start justify-between">
        <span className="font-mono-c text-xs uppercase tracking-[0.16em] text-brand">{c.caseNo}</span>
        <span className="grid h-10 w-10 place-items-center rounded-full border border-line text-content transition-all duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-navy">
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </span>
      </div>

      <div className="mt-8">
        <h3 className="font-display text-3xl font-normal leading-tight tracking-tight md:text-[2.1rem]">
          {c.client}
        </h3>
        <p className="mt-1 font-mono-c text-xs uppercase tracking-[0.14em] text-mutedc">{c.sector}</p>
        <p className="mt-5 text-base leading-relaxed text-mutedc">
          {c.teaserParts.map((p) => p.t).join("")}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-4 border-t border-line pt-6">
        {c.metrics.map((m) => (
          <div key={m.label}>
            <div className="font-display text-2xl font-semibold text-content">{m.value}</div>
            <div className="mt-1 font-mono-c text-[0.6rem] uppercase leading-tight tracking-[0.1em] text-mutedc">
              {m.label}
            </div>
          </div>
        ))}
      </div>
    </Link>
  );
}

function PlaceholderCard({ p, caseNo }) {
  return (
    <div className="relative flex min-h-[360px] flex-col justify-between rounded border border-line bg-surface-2 p-8 md:p-9">
      <div className="flex items-start justify-between">
        <span className="font-mono-c text-xs uppercase tracking-[0.16em] text-brand">{caseNo}</span>
        <span className="grid h-10 w-10 place-items-center rounded-full border border-line text-mutedc">
          <Clock size={15} />
        </span>
      </div>
      <div className="mt-8">
        <h3 className="font-display text-3xl font-normal leading-tight tracking-tight text-content md:text-[2.1rem]">
          {p.sector}
        </h3>
        <p className="mt-5 max-w-xs text-base leading-relaxed text-mutedc">{p.hint}</p>
      </div>
      <div className="mt-8 border-t border-line pt-6">
        <span className="inline-flex items-center gap-2 font-mono-c text-[0.62rem] uppercase tracking-[0.14em] text-mutedc">
          <span className="h-1.5 w-1.5 rounded-full bg-brand" />
          Case study coming soon
        </span>
      </div>
    </div>
  );
}

export function ClosedLoops() {
  return (
    <Section id="work" className="py-24 md:py-32">
      <div>
        <Eyebrow>Selected work</Eyebrow>
        <h2 className="mt-6 font-display text-4xl font-light tracking-[-0.015em] text-content sm:text-5xl md:text-6xl">
          Closed <span className="emph">Loops</span>
        </h2>
      </div>

      <Stagger className="mt-14 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-2 lg:grid-cols-3" stagger={0.12}>
        {CASES.map((c) => (
          <StaggerItem key={c.slug}>
            <RealCard c={c} />
          </StaggerItem>
        ))}
        {PLACEHOLDERS.map((p) => (
          <StaggerItem key={p.sector}>
            <PlaceholderCard p={p} caseNo={p.caseNo} />
          </StaggerItem>
        ))}
      </Stagger>
    </Section>
  );
}
